// pages/UserTransactions.js - User transaction history page
import React, { useState, useEffect } from 'react';
import { Coins, TrendingUp, Gift, Eye, RefreshCw, Award, DollarSign, ArrowDownLeft } from 'lucide-react';
import transactionService from '../services/TransactionService';
import { useAuth } from '../context/AuthContext';

function UserTransactions() {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [balance, setBalance] = useState(0);
  const [totalEarned, setTotalEarned] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user) {
      fetchUserData();
    }
  }, [user]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch balance and transactions in parallel
      const [balanceResult, transactionsResult] = await Promise.all([
        transactionService.getUserBalance(user.uid),
        transactionService.getUserTransactions(user.uid)
      ]);

      if (balanceResult.success) {
        setBalance(balanceResult.balance);
        setTotalEarned(balanceResult.totalEarned);
      }

      if (transactionsResult.success) {
        setTransactions(transactionsResult.data);
      } else {
        setError(transactionsResult.error);
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
      setError('Failed to load transaction data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString() + ' ' + dateObj.toLocaleTimeString();
  };

  const getTransactionIcon = (type) => {
    const typeInfo = transactionService.getTransactionTypeInfo(type);
    const iconMap = {
      Award,
      DollarSign,
      ArrowDownLeft,
      Coins
    };
    return iconMap[typeInfo.iconName] || Coins;
  };

  const getTransactionColor = (type) => {
    const typeInfo = transactionService.getTransactionTypeInfo(type);
    return typeInfo.color;
  };

  const getTransactionBgColor = (type) => {
    const typeInfo = transactionService.getTransactionTypeInfo(type);
    return typeInfo.bgColor;
  };

  const getTransactionLabel = (type) => {
    const typeInfo = transactionService.getTransactionTypeInfo(type);
    return typeInfo.label;
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Coins className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-600 mb-2">Please Log In</h3>
          <p className="text-gray-500">You need to log in to view your transaction history.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white py-6">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold flex items-center">
                <Coins className="mr-3" size={28} />
                My Wallet
              </h1>
              <p className="mt-1 text-green-100">
                Track your earnings and transaction history
              </p>
            </div>
            <button
              onClick={fetchUserData}
              className="flex items-center px-3 py-2 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-colors"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="ml-2 hidden md:block">Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Balance Cards */}
      <div className="container mx-auto px-4 py-6">
        <div className="mb-8">
          {/* Mobile: Side by side layout */}
          <div className="md:hidden bg-white rounded-lg shadow-sm p-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Current Balance */}
              <div className="text-center">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <Coins className="w-5 h-5 text-green-600" />
                </div>
                <div className="text-xs font-medium text-gray-500">Current Balance</div>
                <div className="text-lg font-bold text-gray-900">
                  {transactionService.formatCurrency(balance)}
                </div>
              </div>

              {/* Total Earned */}
              <div className="text-center">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
                <div className="text-xs font-medium text-gray-500">Total Earned</div>
                <div className="text-lg font-bold text-gray-900">
                  {transactionService.formatCurrency(totalEarned)}
                </div>
              </div>
            </div>
          </div>

          {/* Desktop: Original layout */}
          <div className="hidden md:grid grid-cols-2 gap-6">
            {/* Current Balance */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <Coins className="w-6 h-6 text-green-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <div className="text-sm font-medium text-gray-500">Current Balance</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {transactionService.formatCurrency(balance)}
                  </div>
                </div>
              </div>
            </div>

            {/* Total Earned */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <div className="text-sm font-medium text-gray-500">Total Earned</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {transactionService.formatCurrency(totalEarned)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Transaction History */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Transaction History</h3>
          </div>

          {loading && (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading transactions...</p>
            </div>
          )}

          {error && !loading && (
            <div className="p-8 text-center">
              <div className="text-red-600 mb-4">{error}</div>
              <button
                onClick={fetchUserData}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          )}

          {!loading && !error && transactions.length === 0 && (
            <div className="p-8 text-center">
              <Gift className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-600 mb-2">No Transactions Yet</h3>
              <p className="text-gray-500">
                Start earning by reporting incorrect promotion information!
              </p>
            </div>
          )}

          {!loading && !error && transactions.length > 0 && (
            <div className="divide-y divide-gray-200">
              {transactions.map((transaction) => {
                const IconComponent = getTransactionIcon(transaction.type);
                return (
                  <div key={transaction.id} className="p-4 md:p-6 hover:bg-gray-50 transition-colors">
                    {/* Mobile Layout */}
                    <div className="md:hidden">
                      {/* First Row: Type, Amount, Status Badge */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getTransactionBgColor(transaction.type)} mr-3`}>
                            <IconComponent className={`w-4 h-4 ${getTransactionColor(transaction.type)}`} />
                          </div>
                          <span className="text-sm font-medium text-gray-900">
                            {getTransactionLabel(transaction.type)}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className={`text-sm font-bold ${getTransactionColor(transaction.type)}`}>
                            +{transactionService.formatCurrency(transaction.amount)}
                          </div>
                        </div>
                      </div>
                      
                      {/* Second Row: Description */}
                      <div className="text-sm text-gray-500 mb-2 ml-11">
                        {transaction.description}
                      </div>
                      
                      {/* Third Row: Date and Status Badge */}
                      <div className="flex items-center justify-between ml-11">
                        <div className="text-xs text-gray-400">
                          {formatDate(transaction.createdAt)}
                        </div>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {transaction.status}
                        </span>
                      </div>
                    </div>

                    {/* Desktop Layout */}
                    <div className="hidden md:flex items-center justify-between">
                      <div className="flex items-center">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getTransactionBgColor(transaction.type)}`}>
                          <IconComponent className={`w-5 h-5 ${getTransactionColor(transaction.type)}`} />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {getTransactionLabel(transaction.type)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {transaction.description}
                          </div>
                          <div className="text-xs text-gray-400 mt-1">
                            {formatDate(transaction.createdAt)}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-lg font-bold ${getTransactionColor(transaction.type)}`}>
                          +{transactionService.formatCurrency(transaction.amount)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {transaction.status}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Earning Info */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <Gift className="h-5 w-5 text-blue-500 mr-2" />
            <div className="text-sm text-blue-700">
              <strong>How to earn:</strong> Report incorrect promotion information and earn IDR 5,000 when your report is reviewed by our team.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserTransactions;